import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useMunicipalStore } from "../../store/municipalStore";
import StatusBadge from "../shared/StatusBadge";
import Button from "../shared/Button";
import { SidePanel } from "../shared/SidePanel";
import { Header, Container } from "../shared/Layout";
import { ChevronRight, MessageCircle } from "lucide-react";

const STATE_ORDER = ["enviado", "clasificado", "asignado", "en_proceso", "resuelto"];

export default function AlcaldeSolicitudes() {
  const requests = useMunicipalStore(state => state.requests);
  const { updateState, addResponse } = useMunicipalStore();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [filterState, setFilterState] = useState("todos");
  const [newResponse, setNewResponse] = useState("");

  const requestsByState = useMemo(() => {
    const grouped = {};
    STATE_ORDER.forEach(state => {
      grouped[state] = requests.filter(r => r.estado === state);
    });
    return grouped;
  }, [requests]);

  const handleStateChange = (requestId, newState) => {
    updateState(requestId, newState);
    if (selectedRequest?.id === requestId) {
      setSelectedRequest({ ...selectedRequest, estado: newState });
    }
  };

  const handleAddResponse = (requestId) => {
    if (newResponse.trim()) {
      addResponse(requestId, newResponse);
      setNewResponse("");
      if (selectedRequest?.id === requestId) {
        const updated = requests.find(r => r.id === requestId);
        setSelectedRequest(updated);
      }
    }
  };

  const kanbanColumns = [
    { key: "enviado", label: "Recibidas", color: "blue" },
    { key: "clasificado", label: "Clasificadas", color: "purple" },
    { key: "asignado", label: "Asignadas", color: "yellow" },
    { key: "en_proceso", label: "En Proceso", color: "orange" },
    { key: "resuelto", label: "Resueltas", color: "green" }
  ];

  return (
    <>
      <Container>
        <Header title="Tablero de Solicitudes" subtitle="Gestiona todas las solicitudes municipales" />

        {/* Kanban Board */}
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-6 min-w-max">
            {kanbanColumns.map(column => (
              <motion.div
                key={column.key}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex-shrink-0 w-80"
              >
                <div className="bg-gradient-to-b from-gray-50 to-gray-100 rounded-2xl p-4 h-fit">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-gray-900">
                      {column.label}
                      <span className="ml-2 text-sm font-semibold text-gray-600 bg-white px-3 py-1 rounded-full">
                        {requestsByState[column.key].length}
                      </span>
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {requestsByState[column.key].map(request => (
                      <motion.div
                        key={request.id}
                        layoutId={request.id}
                        whileHover={{ y: -4 }}
                        onClick={() => setSelectedRequest(request)}
                        className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-lg cursor-pointer transition-all group"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm flex-1">{request.tipo}</h4>
                          <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                            request.urgencia === "alta" ? "bg-red-500" :
                            request.urgencia === "media" ? "bg-yellow-500" :
                            "bg-green-500"
                          }`} />
                        </div>
                        <p className="text-xs text-gray-600 mb-3">{request.barrio}</p>
                        <div className="flex items-center justify-between">
                          <StatusBadge estado={request.estado} />
                          <ChevronRight size={16} className="text-gray-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Container>

      {/* Side Panel for Details */}
      <SidePanel
        isOpen={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        title={selectedRequest?.tipo || "Solicitud"}
      >
        {selectedRequest && (
          <div className="space-y-6">
            {/* Request Info */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <p className="text-sm text-blue-900 mb-1">Área</p>
              <p className="font-bold text-blue-900">{selectedRequest.area}</p>
            </div>

            {/* Description */}
            <div>
              <h4 className="font-bold text-gray-900 mb-2">Descripción</h4>
              <p className="text-gray-700 text-sm leading-relaxed">{selectedRequest.descripcion}</p>
            </div>

            {/* Timeline */}
            <div>
              <h4 className="font-bold text-gray-900 mb-4">Historial</h4>
              <div className="space-y-3">
                {selectedRequest.timeline.map((event, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                    <div className="flex-1 pt-1">
                      <p className="font-semibold text-sm text-gray-900 capitalize">
                        {event.state === "en_proceso" ? "En proceso" : event.state}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(event.date).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Responses */}
            <div>
              <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                <MessageCircle size={18} />
                Respuestas ({selectedRequest.respuestas.length})
              </h4>
              <div className="space-y-3 max-h-48 overflow-y-auto mb-4">
                {selectedRequest.respuestas.map(resp => (
                  <div key={resp.id} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <p className="text-xs font-semibold text-blue-900 mb-1">{resp.author}</p>
                    <p className="text-sm text-gray-700">{resp.message}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(resp.date).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              {/* Add Response */}
              <div className="space-y-2">
                <textarea
                  value={newResponse}
                  onChange={(e) => setNewResponse(e.target.value)}
                  placeholder="Escribe una respuesta al ciudadano..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAddResponse(selectedRequest.id)}
                  disabled={!newResponse.trim()}
                  className="w-full"
                >
                  Enviar Respuesta
                </Button>
              </div>
            </div>

            {/* State Change */}
            <div>
              <h4 className="font-bold text-gray-900 mb-3">Cambiar Estado</h4>
              <div className="space-y-2">
                {STATE_ORDER.map(state => (
                  <Button
                    key={state}
                    variant={selectedRequest.estado === state ? "primary" : "outline"}
                    size="sm"
                    onClick={() => handleStateChange(selectedRequest.id, state)}
                    className="w-full"
                  >
                    {state.charAt(0).toUpperCase() + state.slice(1).replace("_", " ")}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </SidePanel>
    </>
  );
}
